import React, { useState } from 'react';
import type { ReactNode } from 'react';
import type { BankItem } from '../types';
import { useBankSelector } from '../hooks/use-bank-selector';
import {
  Wrapper,
  Label,
  Trigger,
  TriggerText,
  Chevron,
  DropdownContainer,
  SearchInput,
  OptionsList,
  OptionItem,
  RadioCircle,
  OptionText,
  LoadingText,
  EmptyText,
  ErrorText,
  BankLogo,
  BankLogoPlaceholder,
} from './bank-selector.styles';

interface BankSelectorProps {
  readonly banks: BankItem[];
  readonly selectedBankId: string;
  readonly onSelect: (bankId: string, bankName: string, bankLogoUrl: string) => void;
  readonly loading: boolean;
  readonly error: string | null | undefined;
  readonly disabled?: boolean;
}

export function BankSelector({ banks, selectedBankId, onSelect, loading, error, disabled }: BankSelectorProps): ReactNode {
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());

  const handleLogoError = (bankCode: string) => {
    setFailedLogos(prev => new Set(prev).add(bankCode));
  };

  const {
    isOpen,
    searchQuery,
    filteredBanks,
    selectedBank,
    wrapperRef,
    handleToggle,
    handleSelect,
    handleSearchChange,
  } = useBankSelector(banks, selectedBankId, onSelect, loading);

  return (
    <Wrapper ref={wrapperRef}>
      <Label htmlFor="bank-selector-trigger">Instituição</Label>
      <Trigger id="bank-selector-trigger" type="button" onClick={handleToggle} isOpen={isOpen && !disabled} disabled={disabled}>
        <TriggerText hasValue={!!selectedBank}>
          {selectedBank ? selectedBank.name : 'Selecione sua instituição'}
        </TriggerText>
        <Chevron isOpen={isOpen && !disabled}>
          <ChevronSvg />
        </Chevron>
      </Trigger>

      {isOpen && !disabled && (
        <DropdownContainer>
          <SearchInput
            type="text"
            placeholder="Ex.: Nubank"
            value={searchQuery}
            onChange={handleSearchChange}
            autoFocus
          />
          <OptionsList>
            {loading && <LoadingText>Carregando...</LoadingText>}
            {!loading && error && (
              <ErrorText>{error}</ErrorText>
            )}
            {!loading && !error && filteredBanks.length === 0 && (
              <EmptyText>Nenhuma instituição encontrada.</EmptyText>
            )}
            {!loading && !error && filteredBanks.map(bank => (
              <OptionItem
                key={bank.code}
                type="button"
                onClick={() => handleSelect(bank)}
                isSelected={bank.code === selectedBankId}
              >
                <RadioCircle isSelected={bank.code === selectedBankId} />
                {bank.logo_url && !failedLogos.has(bank.code) ? (
                  <BankLogo src={bank.logo_url} alt={bank.name} onError={() => handleLogoError(bank.code)} />
                ) : (
                  <BankLogoPlaceholder>{bank.name.charAt(0)}</BankLogoPlaceholder>
                )}
                <OptionText>{bank.name}</OptionText>
              </OptionItem>
            ))}
          </OptionsList>
        </DropdownContainer>
      )}
    </Wrapper>
  );
}

function ChevronSvg(): ReactNode {
  return (
    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
